import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, SelectProps, Spin, Select, Form, Tag } from 'antd'
import { FormInstance } from 'antd/lib'
import { useTheme } from 'antd-style'
import { useEffect, useMemo, useState } from 'react'

import { useClassifierServiceGetCommonClassifierElementListEndpointKey } from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierService,
  ClassifierType,
  GetIcd10AmClassifierElementListResponseItem,
  Icd10AmElementType
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { Text } from 'src/components'
import { ValueLabelType } from 'src/features/persons/types'
import { useClassifierId, useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

import { useStyles } from './styles'

type FullDiagnosis = {
  id?: string
  code?: string
  name?: { ro?: string; ru?: string }
}

type Props = {
  form: FormInstance
  fieldName: string | string[]
  /** 'label' (default): writes [{value,label}]; 'full': writes [{id,code,name}] matching API shape. */
  valueShape?: 'label' | 'full'
  /**
   * Without a search, list only the ICD-10-AM elements of this special list (e.g. the opportunistic
   * diseases of Form 058-1/e); a search still looks in the whole ICD-10-AM.
   */
  suggestedType?: Icd10AmElementType
} & SelectProps

export const OpportunisticDiseaseInfiniteSelect = ({
  form,
  fieldName,
  placeholder,
  valueShape = 'label',
  suggestedType,
  ...rest
}: Props) => {
  const { styles } = useStyles()
  const palette = useTheme()

  const { currentLang } = useLocales()
  const [selectedValues, setSelectedValues] = useState<ValueLabelType[]>([])
  const [search, setSearch] = useState<string>()

  const debouncedSearch = useDebounce(search)
  const type = debouncedSearch ? undefined : suggestedType

  const id = useClassifierId(
    ClassifierType.IC_D10AM,
    ClassifierCategory.DIAGNOSTIC
  )

  const diseases = Form.useWatch(fieldName, form)

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useClassifierServiceGetCommonClassifierElementListEndpointKey,
      String(id),
      debouncedSearch,
      type
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response =
        await ClassifierService.getIcd10AmClassifierElementListEndpoint(
          String(id),
          pageParam,
          DEFAULT_PER_PAGE,
          undefined,
          debouncedSearch,
          type
        )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetIcd10AmClassifierElementListResponseItem[] | undefined
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1,
    enabled: !!id
  })

  const selectOptions = useMemo(
    () => [
      ...(data?.pages.flatMap(
        (page) =>
          page.data?.map(
            (item: GetIcd10AmClassifierElementListResponseItem) => ({
              value: item?.id,
              label: `${item.code} - ${item?.translation?.[currentLang?.value]}`,
              full: {
                id: item.id,
                code: item.code,
                name: item.translation
              } as FullDiagnosis
            })
          ) || []
      ) || [])
    ],
    [data, currentLang]
  )

  const isFull = valueShape === 'full'

  const toDisplay = (item: FullDiagnosis): ValueLabelType => ({
    value: item.id ?? '',
    label: `${item.code ?? ''} - ${item.name?.[currentLang?.value] ?? ''}`
  })

  const onSelect = (value: string) => {
    const option = selectOptions?.find((o) => o?.value === value)
    if (!option) return

    if (isFull) {
      const current = (diseases as FullDiagnosis[] | undefined) ?? []
      form.setFieldValue(fieldName, [...current, option.full])
    } else {
      form.setFieldValue(fieldName, [...(selectedValues || []), option])
    }
  }

  const onDeselect = (value?: string) => {
    if (isFull) {
      const current = (diseases as FullDiagnosis[] | undefined) ?? []
      const updated = current.filter((item) => item.id !== value)
      form.setFieldValue(fieldName, updated.length ? updated : undefined)
      return
    }

    const updatedValues = diseases.filter(
      (item: ValueLabelType) => item.value !== value
    )
    form.setFieldValue(
      fieldName,
      updatedValues.length ? updatedValues : undefined
    )
  }

  useEffect(() => {
    if (isFull) {
      const current = (diseases as FullDiagnosis[] | undefined) ?? []
      setSelectedValues(current.map(toDisplay))
    } else {
      setSelectedValues(diseases)
    }
  }, [diseases, isFull, currentLang])

  return (
    <Flex vertical gap={16}>
      <Select
        mode="multiple"
        value={selectedValues?.map((v) => v.value)}
        maxTagCount={0}
        tagRender={() => (
          <Text
            color={palette.colorTextPlaceholder}
            className={styles.selectPlaceholder}
          >
            {placeholder}
          </Text>
        )}
        allowClear={false}
        filterOption={false}
        options={selectOptions}
        loading={isFetching}
        placeholder={placeholder}
        showSearch
        onSelect={onSelect}
        onDeselect={onDeselect}
        onSearch={(value) => setSearch(value)}
        onClear={() => setSearch(undefined)}
        onPopupScroll={(e) => {
          const target = e.target as HTMLElement
          if (
            Math.ceil(target.scrollTop + target.offsetHeight) ===
            target.scrollHeight
          )
            fetchNextPage()
        }}
        notFoundContent={
          isFetching ? (
            <Flex justify="center">
              <Spin spinning />
            </Flex>
          ) : (
            <Empty />
          )
        }
        {...rest}
      />

      <Flex wrap="wrap" gap="8px 0px">
        {selectedValues?.map((item) => (
          <Tag
            key={item.value}
            closable
            className={styles.tag}
            onClose={() => onDeselect(item.value)}
          >
            {item.label}
          </Tag>
        ))}
      </Flex>
    </Flex>
  )
}
